import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { displayToast } from "../utils/displayToast";
import {
  checkboxFields,
  formFields,
  defaultFormData,
  editableFields,
} from "../constants/FieldMasterConfigurationConstants";
import { capitalizeFirstLetterOfEachWord } from "../utils/Formatter";
import { createFieldmaster, updateFieldmaster } from "../apis/fieldMaster";
import "../styles/components/FieldMaster.css";

const CustomSelect = ({ options, value, onChange, placeholder, disabled }) => {
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const wrap = wrapperRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;

    const rect = wrap.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;

    const margin = 12;
    const availableBelow = Math.max(0, spaceBelow - margin);
    const availableAbove = Math.max(0, spaceAbove - margin);

    if (availableBelow < 200 && availableAbove > availableBelow) {
      setOpenUp(true);
      list.style.maxHeight = `${Math.min(availableAbove, 360)}px`;
    } else {
      setOpenUp(false);
      list.style.maxHeight = `${Math.min(
        availableBelow || viewportH - rect.top - 80,
        360
      )}px`;
    }

    setTimeout(() => {
      list && (list.style.willChange = "transform");
    }, 10);
  }, [open]);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((v) => !v);
    }
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className="field-master-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`field-master-select-toggle ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span
          className={`field-master-select-value ${!selectedLabel ? "placeholder" : ""}`}
        >
          {selectedLabel || placeholder}
        </span>
        <span className="field-master-select-caret" />
      </button>

      <ul
        ref={listRef}
        className={`field-master-select-list ${open ? "visible" : ""} ${
          openUp ? "open-up" : "open-down"
        }`}
        role="listbox"
        tabIndex={-1}
      >
        {options.map((opt) => (
          <li
            key={opt.value || opt.label}
            role="option"
            aria-selected={value === opt.value}
            className={`field-master-select-option ${
              value === opt.value ? "selected" : ""
            }`}
            onClick={() => handleSelect(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect(opt.value);
              }
            }}
            tabIndex={0}
          >
            {opt.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const FieldMaster = () => {
  const navigate = useNavigate();
  const { masterData } = useLocation().state || {};
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const numberFields = ["field_length", "new_form_id", "order_no"];

  const defaultItems = {
    enter_validation: {
      db_query: "",
      is_db_query: false,
      field_updation: [
        {
          copy_from: "db",
          copy_to_field_name: "",
          copy_to_table_type: "",
          copy_from_field_name: "",
        },
      ],
    },
    leave_validation: {
      db_query: "",
      is_db_query: false,
      field_updation: [
        {
          copy_from: "db",
          copy_to_field_name: "",
          copy_to_table_type: "",
          copy_from_field_name: "",
        },
      ],
    },
  };

  const updationFields = [
    { key: "copy_from", placeholder: "Copy From", type: "text" },
    {
      key: "copy_to_field_name",
      placeholder: "Copy To Field Name",
      type: "text",
    },
    {
      key: "copy_to_table_type",
      placeholder: "Copy To Table Type",
      type: "select",
    },
    {
      key: "copy_from_field_name",
      placeholder: "Copy From Field Name",
      type: "text",
    },
  ];

  const addNewItem = (section) => {
    setFormData((prev) => ({
      ...prev,
      [section]: defaultItems[section],
    }));
  };

  useEffect(() => {
    if (masterData && Object.keys(masterData).length > 0) {
      const updatedData = {
        ...masterData,
        new_form_id:
          masterData.new_form_id === 0 ? null : masterData.new_form_id,
        order_no: masterData.order_no === 0 ? null : masterData.order_no,
      };
      setFormData(updatedData);
    }
  }, [masterData]);

  const isEditable = (name) => {
    if (masterData && Object.keys(masterData).length > 0) {
      return editableFields.has(name);
    }
    return true;
  };

  const CheckboxInput = ({ label, name, checked, onChange, disabled }) => (
    <label className="checkbox-label">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      {label}
    </label>
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : numberFields.includes(name)
          ? value === ""
            ? ""
            : parseInt(value, 10)
          : value,
    }));
  };

  const handleValidationChange = (e, section) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleFieldUpdationChange = (section, index, fieldKey, value) => {
    setFormData((prev) => {
      const updatedFieldUpdation = [...prev[section].field_updation];

      updatedFieldUpdation[index] = {
        ...updatedFieldUpdation[index],
        [fieldKey]: value,
      };

      return {
        ...prev,
        [section]: {
          ...prev[section],
          field_updation: updatedFieldUpdation,
        },
      };
    });
  };

  const addNestedItem = (section) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        field_updation: [
          ...prev[section].field_updation,
          {
            copy_from: "db",
            copy_to_field_name: "",
            copy_to_table_type: "",
            copy_from_field_name: "",
          },
        ],
      },
    }));
  };

  const removeNestedItem = (section, indexToRemove) => {
    setFormData((prev) => {
      const currentItems = prev[section].field_updation;

      if (currentItems.length <= 1) {
        displayToast(`At least one  item is required`, "error");
        return prev;
      }

      const updatedItems = currentItems.filter(
        (_, index) => index !== indexToRemove
      );

      return {
        ...prev,
        [section]: {
          ...prev[section],
          field_updation: updatedItems,
        },
      };
    });
  };

  const removeItem = (section) => {
    setFormData((prev) => ({
      ...prev,
      [section]: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasZeroValue = numberFields.some(
      (field) => parseInt(formData[field], 10) === 0
    );

    if (hasZeroValue) {
      displayToast("Number fields cannot have a value of 0.");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        ...numberFields.reduce((acc, field) => {
          acc[field] =
            formData[field] === null || formData[field] === ""
              ? null
              : parseInt(formData[field], 10);
          return acc;
        }, {}),
      };

      if (masterData && Object.keys(masterData).length > 0) {
        const editablePayload = {};

        editableFields.forEach((field) => {
          editablePayload[field] = payload[field];
        });

        const response = await updateFieldmaster(
          editablePayload,
          masterData.id
        );

        if (response.id) {
          displayToast("Field updated successfully!", "success");
          setFormData(defaultFormData);
          navigate("/field-master");
        } else {
          displayToast(
            "Failed to update field: " +
              (response.description || response.message || "Unknown error")
          );
        }
      } else {
        const response = await createFieldmaster(payload);
        if (response.id) {
          displayToast("Field added successfully!", "success");
          setFormData(defaultFormData);
          navigate("/field-master");
        } else {
          displayToast(
            "Failed to add field: " +
              (response.description || response.message || "Unknown error")
          );
        }
      }
    } catch (error) {
      console.error("Error submitting field:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="field-master-container">
      <h2 className="card-title">Field Master Configuration</h2>
      <div className="field-master-card">
        <form onSubmit={handleSubmit} className="field-master-form">
          <div className="checkbox-grid">
            {checkboxFields.map((field) => (
              <CheckboxInput
                key={field.name}
                label={field.label}
                name={field.name}
                checked={formData[field.name]}
                onChange={handleChange}
                disabled={!isEditable(field.name)}
              />
            ))}
          </div>
          <div className="form-grid">
            {formFields.map((field) => {
              if (field.type === "text") {
                return (
                  <div key={field.name} className="form-group">
                    <label>{field.label}</label>
                    <input
                      type={
                        numberFields.includes(field.name) ? "number" : "text"
                      }
                      name={field.name}
                      value={
                        field.name === "enter_validation" ||
                        field.name === "leave_validation"
                          ? formData[field.name]?.db_query || null
                          : formData[field.name]
                      }
                      onChange={handleChange}
                      className="form-input"
                      required={field.required}
                      placeholder={field.label}
                      disabled={!isEditable(field.name)}
                    />
                  </div>
                );
              } else if (field.type === "select") {
                const selectOptions = [
                  { value: "", label: `Select ${field.label}` },
                  ...field.options
                ];
                
                return (
                  <div key={field.name} className="form-group">
                    <label>{field.label}</label>
                    <CustomSelect
                      options={selectOptions}
                      value={formData[field.name]}
                      onChange={(value) => {
                        const event = {
                          target: {
                            name: field.name,
                            value: value,
                            type: "select"
                          }
                        };
                        handleChange(event);
                      }}
                      placeholder={`Select ${field.label}`}
                      disabled={!isEditable(field.name)}
                    />
                  </div>
                );
              }
                return null;
            })}
          </div>
          <div className="validation-container">
              {formFields.map((field) => {
                if (field.type === "validation") {
                  const validationData = formData[field.name];
                return (
                  <div key={field.name} className="form-group">
                    <label>{field.label}</label>
                    <div className="card-body card-body-no-padding">
                      {validationData ? (
                        <div className="pickup-data-container">
                          <div className="data-row">
                            <div className="row-inputs">
                              <input
                                type="text"
                                name="db_query"
                                placeholder="Db Query"
                                value={validationData.db_query || ""}
                                onChange={(e) =>
                                  handleValidationChange(e, field.name)
                                }
                                required
                              />
                              <div className="row-inputs checkbox-row">
                                <div className="checkbox-wrapper">
                                  <input
                                    type="checkbox"
                                    name="is_db_query"
                                    checked={validationData.is_db_query || false}
                                    onChange={(e) =>
                                      handleValidationChange(e, field.name)
                                    }
                                    id={`is_db_query_${field.name}`}
                                  />
                                  <label
                                    className="checkbox-label"
                                    htmlFor={`is_db_query_${field.name}`}
                                  >
                                    Is Db Query
                                  </label>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => removeItem(field.name)}
                                  className="remove-btn remove-btn-no-flex"
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="fields-section">
                            <div className="fields-header">
                              <h4>Field updation</h4>
                            </div>
                            <div className="data-rows">
                              {validationData.field_updation?.map(
                                (item, index) => (
                                  <div key={index} className="data-row">
                                    <div className="row-inputs">
                                      {updationFields.map((updatedField) => {
                                        if (updatedField.type === "text") {
                                          return (
                                            <input
                                              key={updatedField.key}
                                              type="text"
                                              placeholder={
                                                updatedField.placeholder
                                              }
                                              value={
                                                item[updatedField.key] || ""
                                              }
                                              onChange={(e) =>
                                                handleFieldUpdationChange(
                                                  field.name,
                                                  index,
                                                  updatedField.key,
                                                  e.target.value
                                                )
                                              }
                                              readOnly={
                                                updatedField.key === "copy_from"
                                              }
                                              required
                                            />
                                          );
                                        } else if (
                                          updatedField.type === "select"
                                        ) {
                                          const tableTypeOptions = [
                                            { value: "", label: "Select Table Type" },
                                            { value: "head", label: "Head" },
                                            { value: "item", label: "Item" }
                                          ];
                                          
                                          return (
                                            <CustomSelect
                                              key={updatedField.key}
                                              options={tableTypeOptions}
                                              value={item[updatedField.key] || ""}
                                              onChange={(value) =>
                                                handleFieldUpdationChange(
                                                  field.name,
                                                  index,
                                                  updatedField.key,
                                                  value
                                                )
                                              }
                                              placeholder="Select Table Type"
                                            />
                                          );
                                        } else {
                                          return null;
                                        }
                                      })}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeNestedItem(field.name, index)
                                        }
                                        className="remove-btn remove-btn-no-flex"
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                )
                              )}

                              <button
                                type="button"
                                onClick={() => addNestedItem(field.name)}
                                className="add-btn"
                              >
                                ＋ Add New Field
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => addNewItem(field.name)}
                          className="add-btn add-btn-no-margin"
                        >
                          ＋ Add {capitalizeFirstLetterOfEachWord(field.name)}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
                return null;
            })}
          </div>
          <div className="field-submit-button-container">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/field-master")}
            >
              ✕ Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isLoading ? (
                <span className="loader"></span>
              ) : (
                "✓ Save Transaction"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FieldMaster;
